// src/App.js
import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import {
  ThemeProvider,
  createTheme,
  CssBaseline,
  GlobalStyles,
  Box,
} from '@mui/material';
import { AnimatePresence } from 'framer-motion';
import { CartProvider } from './components/shopper/ShopperCart';
import MainLoader from './components/loaders/MainLoader';
import Header from './components/main/Header';
import Hero from './components/main/Hero';
import Harvest from './components/main/Harvest';
import About from './components/main/About';
import Reserve from './components/main/Reserve';
import OurHeritage from './components/pages/OurHeritage';
import OurReserve from './components/pages/OurReserve';
import Products from './components/pages/Products';
import Catalog from './components/pages/Catalog';
import Contact from './components/pages/Contact';
import ShopHarvest from './components/pages/ShopHarvest';
import ShopGiftCard from './components/pages/ShopGiftCard';
import ShopperCartPage from './components/shopper/ShopperCart';
import ShopperCheckout from './components/shopper/ShopperCheckout';
import ShopperConfirmation from './components/shopper/ShopperConfirmation';
import Orders from './components/admin/Orders';
import OrderComplete from './components/shopper/OrderComplete';
import OrderSuccess from './components/shopper/OrderSuccess';
import OrderFailed from './components/shopper/OrderFailed';
import Footer from './components/main/Footer';
import PrivacyPolicy from './components/pages/PrivacyPolicy';
import TermsAndConditions from './components/pages/Terms';
import ReturnPolicy from './components/pages/ReturnPolicy';
import SeoHead from './components/seo/SeoHead';
import SearchOrder from './components/pages/SearchOrder';
import { bottles } from './data/Bottles';
import GAdsPageView from './GAdsPageView';


const theme = createTheme({
  palette: {
    mode: 'light',
    primary:   { main: '#11403b' },
    secondary: { main: '#c79a3d' },
    background:{ default: '#f8f6f2' },
  },
  typography: {
    fontFamily: 'Inter, sans-serif',
    allVariants:{ color: '#1a1a1a' },
    h1: { fontFamily: '"Playfair Display", serif', fontWeight: 600 },
    h2: { fontFamily: '"Playfair Display", serif', fontWeight: 600 },
    h3: { fontFamily: '"Playfair Display", serif', fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
});

function Home() {
  return (
    <>
      <SeoHead
        title="Marrakech Reserve – Premium Moroccan Olive Oil"
        description="Buy Moroccan olive oil. Award‑winning extra‑virgin olive oils, crafted from hand‑harvested olives in the historic groves of Marrakech. Shop gourmet olive oils, limited‑edition harvest releases, and gift cards."
        url="https://marrakech-reserve.com/"
        keywords={
          "Buy Moroccan olive oil, olive oil from Morocco, Marrakech olive oil, extra virgin olive oil, premium olive oil, Moroccan olive oil, hand-harvested olives, cold-pressed oil, small-batch olive oil, gourmet olive oil, olive oil gifts, sustainable olive farming, Marrakech Reserve "
        }
      />
      <Hero />
      <Reserve />
      <Box
        sx={{
          position: 'relative',
          py: { xs: 6, md: 10 },
          backgroundColor: theme.palette.background.default,
        }}
      >
        <Harvest bottles={bottles} />
      </Box>
      <About />
    </>
  );
}

export default function App() {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setLoading(false), 3500);
    return () => clearTimeout(timer);
  }, []);

  return (
    <CartProvider>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <GlobalStyles styles={{
          'html, body, #root': {
            height: '100%',      
            width: '100vw',
            overflowX: 'hidden',
          },
        }} />

        <AnimatePresence>
          {loading && <MainLoader key="main-loader" />}
        </AnimatePresence>

        {!loading && (
          <BrowserRouter>
            <GAdsPageView />
            <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>

              <Header />

              {/* Main content grows to push footer to bottom on short pages */}
              <Box component="main" sx={{ flexGrow: 1 }}>
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/our-heritage" element={<OurHeritage />} />
                  <Route path="/search-order" element={<SearchOrder />} />
                  <Route path="/our-reserve" element={<OurReserve />} />
                  <Route path="/contact-us" element={<Contact />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                  <Route path="/shop-giftcard" element={<ShopGiftCard />} />
                  <Route path="/products" element={<Catalog />} />
                  <Route path="/products/:id" element={<Products />} />
                  <Route path="/harvest/:year" element={<ShopHarvest />} />
                  <Route path="/cart" element={<ShopperCartPage />} />
                  <Route path="/checkout" element={<ShopperCheckout />} />
                  <Route path="/order-confirmation" element={<ShopperConfirmation />} />
                  <Route path="/order-confirmation/complete" element={<OrderComplete />} />
                  <Route path="/order-confirmation/success" element={<OrderSuccess />} />
                  <Route path="/order-confirmation/failed" element={<OrderFailed />} />
                  <Route path="/return-policy" element={<ReturnPolicy />} />
                  <Route path="/admin/orders" element={<Orders />} />
                </Routes>
              </Box>

              <Footer />

            </Box>
          </BrowserRouter>
        )}
      </ThemeProvider>
    </CartProvider>
  );
}
